import {
  ADMIN_ORDER_DETAILS,
  AdminHeader,
  AdminSpinner,
  ArrowRed,
  assignDeliveryToAuthenticatedUser,
  BoxIcon,
  BrandType,
  Button,
  CallRecordingAndTranscription,
  CarWheelIcon,
  Charges,
  ChevronSmall,
  ClockIcon,
  CollapsibleComponent,
  DeliveryCreationType,
  DeliveryProgressBar,
  DeliveryType,
  Disclaimer,
  displayCentsInDollars,
  Div,
  DollarIcon,
  findOrderById,
  formatDateTimeFull,
  formatDateTimeShort,
  getCancelVDriveDeliveryFee,
  getCancelVDriveOrderFee,
  getDeliveriesAssignHistory,
  getInterceptorCall,
  getOrderById,
  getTimeAgoTimeIn,
  getVDriveOffers,
  GrayCircleEarphoneIcon,
  H5,
  HeadphonesIcon,
  Icon,
  Img,
  isAdmin,
  isFutureDate,
  isPartner,
  lightTheme,
  markDeliveryAsReady,
  NoteIcon,
  OptedInIcon,
  Order,
  OrderCourierIcon,
  OrderCustomerIcon,
  OrderInfoIcon,
  OrderNotesIcon,
  P,
  PinIcon,
  renderProviderIcon,
  renderProviderName,
  renderFleetImage,
  renderFleetSource,
  StackIcon,
  StarCircleIcon,
  StarIcon,
  StoreIcon,
  SuccessToast,
  TooltipIcon,
  TruckLeftIcon,
  VDriveDeliveryEntity,
  vDriveDeliveryStatus,
  VDriveGetQuoteHistoryResponse,
  VDriveGetRecordingResponse,
  VDriveOfferEntity,
  reportError,
} from '@vgrubs/components';
import {
  Dispatch,
  lazy,
  SetStateAction,
  Suspense,
  useEffect,
  useRef,
  useState,
} from 'react';
import { formatPhoneNumber } from 'react-phone-number-input';
import { useNavigate, useParams } from 'react-router';
import { CancelVDriveModal } from '../Orders/CancelVDriveModal';
import { ChangeAddress } from '../Orders/ChangeAddress';
import { VDriveAssignDriver } from '../Orders/VDriveAssignDriver';
import { VDriveStatusComponent } from '../Orders/VDriveStatusComponent';
import {
  RenderStatus,
  TimelineCircle,
  TimelineLineBottom,
  TimelineLineTop,
} from './DeliveriesStyled';
import { ClaimDeliveryModal } from './components/AssignDispatcherModal';
import useDeliveriesDetailsSocket from './components/DeliveriesDetailsSocket';
import { DeliveriesDropdown } from './components/DeliveriesDropdown';
import { DeliveriesRequestRefund } from './components/DeliveriesRequestRefund';
import { DeliveryCancelModal } from './components/DeliveryCancelModal';
// Lazy-loaded: leaflet/maplibre access `window` at import time, which breaks
// Next.js prerender in the tracker app (this file is reachable via the
// @vgrubs/components barrel).
const DeliveryMapComponent = lazy(() =>
  import('./components/DeliveryMapComponent').then((m) => ({
    default: m.DeliveryMapComponent,
  }))
);
import { DeliveryStatusPill } from './components/DeliveryStatusPill';
import { DistancePill } from './components/DistancePill';
import { DeliveryInternalNotesModal } from './components/InternalNotesModal';
import { HistoryAssign } from './components/HistoryAssign';

export const handleAssignToAuthenticatedUser = async (
  id: number,
  setData: Dispatch<SetStateAction<VDriveDeliveryEntity | null>>,
  setLoading: Dispatch<SetStateAction<boolean>>,
  setClaimDeliveryModal: Dispatch<SetStateAction<number | null>>
) => {
  if (!id) return;
  try {
    setLoading(true);
    const { data } = await assignDeliveryToAuthenticatedUser(id);
    setData(data.data);
    SuccessToast(data.message);
    setClaimDeliveryModal(null);
  } catch (error) {
    reportError(error);
  } finally {
    setLoading(false);
  }
};

export const DeliveryDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams() as { id: string };
  const [data, setData] = useState<VDriveDeliveryEntity | null>(null);
  const [loading, setLoading] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [refundModal, setRefundModal] = useState(false);
  const [cancelVDriveDeliveryFee, setCancelVDriveDeliveryFee] =
    useState<number>(0);
  const [isDriverAssigned, setIsDriverAssigned] = useState<boolean>(false);
  useDeliveriesDetailsSocket({
    deliveryStatus: data?.status,
    deliveryId: data?.id?.toString() || '',
    getData: getDeliveryDetails,
  });
  const [showInternalNotesModal, setShowInternalNotesModal] = useState(false);
  const [internalNotesCollapse, setInternalNotesCollapse] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const [elementHeight, setElementHeight] = useState(0);

  const [cancelVDriveOrderFee, setCancelVDriveOrderFee] = useState<number>(0);

  useEffect(() => {
    if (ref.current && data?.internalNotes) {
      setElementHeight(ref.current.scrollHeight);
    }
  }, [data?.internalNotes, ref.current]);

  const [interceptorCallData, setInterceptorCallData] =
    useState<VDriveGetRecordingResponse | null>(null);

  const [interceptorCallCollapse, setInterceptorCallCollapse] = useState(false);
  const refInterceptorCall = useRef<HTMLDivElement>(null);
  const [elementHeightInterceptorCall, setElementHeightInterceptorCall] =
    useState(0);

  useEffect(() => {
    if (refInterceptorCall.current && interceptorCallData?.recordingUrl) {
      setElementHeightInterceptorCall(refInterceptorCall.current.scrollHeight);
    }
  }, [interceptorCallData, refInterceptorCall.current]);

  // The backend answers 400 ("Interceptor call ID not found.") whenever no
  // recording exists, so we only ask for one once the delivery tells us there
  // is one. interceptorCallId on its own is not a reliable condition.
  const hasCallRecording = data?.hasCallRecording === true;

  async function getInterceptorCallFunc() {
    if (!id) return;
    if (!isAdmin) return;
    try {
      const { data: interceptorCallData } = await getInterceptorCall(id);
      setInterceptorCallData(interceptorCallData.data);
    } catch (error) {}
  }

  useEffect(() => {
    if (!isAdmin || !id || !hasCallRecording) {
      setInterceptorCallData(null);
      return;
    }
    getInterceptorCallFunc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, hasCallRecording]);

  const [orderData, setOrderData] = useState<Order | null>(null);
  const [changeAddressModal, setChangeAddressModal] = useState<number | null>(
    null
  );

  async function getDeliveryDetails(noLoader?: boolean) {
    try {
      if (!noLoader) {
        setLoading(true);
      }
      const { data: deliveryData } = await findOrderById(id);
      setData(deliveryData.data);

      if (deliveryData.data.orderId) {
        const { data: orderData } = await getOrderById(
          deliveryData.data.orderId
        );
        setOrderData(orderData.data);
      }
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (id) {
      getDeliveryDetails();

      // Start interval to call getDriver every 15 seconds
      const interval = setInterval(() => {
        // getDriver();
        getDeliveryDetails(true);
      }, 15000);

      // Cleanup function to clear interval when component unmounts or id changes
      return () => clearInterval(interval);
    }
  }, [id]); // Re-run effect if id changes

  const RenderProgressBar = ({ status }: { status: vDriveDeliveryStatus }) =>
    RenderStatus(status)?.progressBar;

  const isManual = data?.creationType === DeliveryCreationType.manual;

  const address = isAdmin ? data?.dropoffInfo?.address : data?.address;
  const instructions = isAdmin
    ? data?.dropoffInfo?.instructions
    : data?.instructions;

  const handleGetCancelVDriveDeliveryFee = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const { data } = await getCancelVDriveDeliveryFee(id);
      setCancelVDriveDeliveryFee(data.data.cancellationFee);
      setIsDriverAssigned(data.data.isDriverAssigned);
      setCancelModal(true);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignDispatcher = async () => {
    handleAssignToAuthenticatedUser(
      claimDeliveryModal ?? 0,
      setData,
      setLoading,
      setClaimDeliveryModal
    );
  };

  const [claimDeliveryModal, setClaimDeliveryModal] = useState<number | null>(
    null
  );

  const onUserClick = () => {
    navigate(`/users/${data?.dispatcher?.id}`);
  };

  async function handleGetCancelVDriveOrderFee() {
    if (!orderData?.id) return;
    try {
      setLoading(true);
      const { data } = await getCancelVDriveOrderFee(orderData?.id);
      setCancelVDriveOrderFee(data.data.cancellationFee);
      setIsDriverAssigned(data.data.isDriverAssigned);
      setCancelVDriveModal(true);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  async function getDrivers() {
    if (!orderData?.id) return;
    try {
      setLoading(true);
      const { data } = await getVDriveOffers(orderData?.id);
      if (data?.data?.length > 0) {
        setAssignDriverModal(data.data);
      } else {
        setNoDriverModal(true);
      }
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  const [assignDriverModal, setAssignDriverModal] = useState<
    VDriveOfferEntity[] | null
  >(null);
  const [noDriverModal, setNoDriverModal] = useState(false);
  const [cancelVDriveModal, setCancelVDriveModal] = useState(false);
  const [historyAssignModal, setHistoryAssignModal] = useState<
    VDriveGetQuoteHistoryResponse[] | null
  >(null);

  const handleHistoryAssign = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const { data } = await getDeliveriesAssignHistory(id);
      setHistoryAssignModal(data.data);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsReady = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const { data } = await markDeliveryAsReady(id);
      setData(data.data);
      SuccessToast(data.message);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  };

  const courierName = data?.driver?.name;
  const courierPhoneNumber =
    data?.driver?.phoneNumberForMerchant ?? data?.driver?.phone;

  const formattedCourierPhoneNumber = courierPhoneNumber?.startsWith('+')
    ? courierPhoneNumber
    : '+' + courierPhoneNumber;

  const formattedCustomerPhoneNumber = data?.customer?.phone?.startsWith('+')
    ? data?.customer?.phone
    : '+' + data?.customer?.phone;

  return (
    <>
      {loading && <AdminSpinner />}

      <AdminHeader title="Delivery Details">
        <Div $flex $alignCenter $gap="16px">
          <DeliveriesDropdown
            data={data}
            setRefundModal={setRefundModal}
            handleGetCancelVDriveDeliveryFee={handleGetCancelVDriveDeliveryFee}
            handleHistoryAssign={handleHistoryAssign}
            handleClaimDelivery={() => setClaimDeliveryModal(data?.id ?? null)}
            handleMarkAsReady={handleMarkAsReady}
          />
        </Div>
      </AdminHeader>

      {data && (
        <>
          <Div $flex $gap="40px" $gapSM="20px" $mt="32" $mSM="20px 0" $columnSM>
            <Div $width="45%" $widthSM="100%">
              <Div $box $p="24px">
                <H5 $mb="24">Order Info</H5>

                {!isManual && (
                  <>
                    <InfoComponent
                      Icon={OrderInfoIcon}
                      title="Order ID"
                      text={data?.orderId}
                      hasBorderTop
                      component={
                        <Icon
                          $pointer
                          onClick={() => {
                            navigate(
                              `${ADMIN_ORDER_DETAILS.replace(
                                ':id',
                                data?.orderId?.toString() || ''
                              )}`
                            );
                          }}
                        >
                          <ArrowRed />
                        </Icon>
                      }
                    />
                    <InfoComponent
                      Icon={OrderInfoIcon}
                      title="Order Source"
                      text={renderProviderName(data?.source)}
                      component={
                        <Img
                          alt=""
                          $width="50px"
                          src={renderProviderIcon(data?.source)}
                        />
                      }
                    />
                  </>
                )}

                <InfoComponent
                  Icon={OrderInfoIcon}
                  title="DSP ID"
                  text={
                    <>
                      {`${data?.dspId ?? '-'}`} <br />
                      {data?.dspCustomerId && `${data?.dspCustomerId}`}
                    </>
                  }
                />
                <InfoComponent
                  Icon={ClockIcon}
                  title="Requested"
                  text={formatDateTimeFull(
                    data?.timestamps?.created_at,
                    data?.timeZone
                  )}
                />
                <InfoComponent
                  Icon={OrderCourierIcon}
                  title="Pickup"
                  text={formatDateTimeFull(
                    data?.timestamps?.pickup_estimate,
                    data?.timeZone
                  )}
                />
                <InfoComponent
                  Icon={TruckLeftIcon}
                  title="Dropoff"
                  text={formatDateTimeFull(
                    data?.timestamps?.dropoff_estimate,
                    data?.timeZone
                  )}
                />
                <InfoComponent
                  Icon={CarWheelIcon}
                  title="Driver"
                  text={
                    <>
                      {`${
                        courierName?.length && courierName?.length > 2
                          ? courierName
                          : '-'
                      }`}{' '}
                      <br />
                      {`${
                        courierPhoneNumber?.length &&
                        courierPhoneNumber?.length > 2
                          ? formattedCourierPhoneNumber
                          : '-'
                      }`}
                    </>
                  }
                />
                <InfoComponent
                  Icon={OrderCustomerIcon}
                  title="Customer"
                  text={
                    <>
                      {`${data?.customer?.name ?? '-'}`} <br />
                      {`${formattedCustomerPhoneNumber ?? '-'}`}
                    </>
                  }
                />
                <InfoComponent
                  Icon={StoreIcon}
                  title="Merchant Name"
                  text={data?.pickupInfo?.name ?? '-'}
                />
                <InfoComponent
                  Icon={BoxIcon}
                  title="Fleet"
                  component={
                    <Img
                      alt=""
                      $width="50px"
                      src={
                        // data?.provider === vDriveProviders.MOTOCLICK &&
                        // data?.vDriveSource === VDriveSource.motoclick
                        //   ? dspLogo
                        //   :
                        renderFleetImage(data?.provider)
                      }
                    />
                  }
                />
                <InfoComponent
                  Icon={StarCircleIcon}
                  title="Rating"
                  text={
                    data?.driverRating?.comment
                      ? `Comment: ${data?.driverRating?.comment ?? '-'}`
                      : undefined
                  }
                  component={
                    <Div $flex $gap="4px" $alignCenter>
                      <Icon
                        $size={24}
                        color={
                          data?.driverRating?.rating
                            ? lightTheme.yellow
                            : lightTheme.lightGray
                        }
                      >
                        <StarIcon />
                      </Icon>
                      <H5 style={{ fontWeight: 'normal' }} $m="0" $mt="1">
                        {data?.driverRating?.rating ?? '-'}
                      </H5>
                    </Div>
                  }
                />
                <InfoComponent
                  Icon={NoteIcon}
                  title="Instructions"
                  text={instructions ?? '-'}
                />
                {data?.specialOfferPhoneNumber && (
                  <InfoComponent
                    Icon={OptedInIcon}
                    title="Opted-in Number"
                    text={formatPhoneNumber(data?.specialOfferPhoneNumber)}
                  />
                )}

                <InfoComponent
                  Icon={PinIcon}
                  title="Address"
                  text={address ?? '-'}
                />
                {isAdmin && (
                  <InfoComponent
                    Icon={StackIcon}
                    title="Delivery Source"
                    text={
                      // data?.provider === vDriveProviders.MOTOCLICK &&
                      // data?.vDriveSource === VDriveSource.motoclick
                      //   ? 'vDrive'
                      //   :
                      renderFleetSource(data?.vDriveSource)
                    }
                  />
                )}
                {isAdmin && data?.dispatcher && (
                  <InfoComponent
                    Icon={HeadphonesIcon}
                    title="Assigned Dispatcher"
                    text={`${data?.dispatcher?.firstName} ${data?.dispatcher?.lastName}`}
                    component={
                      <Icon $pointer onClick={onUserClick}>
                        <ArrowRed />
                      </Icon>
                    }
                  />
                )}

                {isAdmin && (
                  <Div $flex $column $p="12px 0" $borderBottom>
                    <Div
                      $pointer
                      $flex
                      $alignCenter
                      $between
                      onClick={() => setInternalNotesCollapse((prev) => !prev)}
                    >
                      <InfoComponent
                        hasBorderBottom={false}
                        Icon={OrderNotesIcon}
                        title="Internal Notes"
                        padding="0"
                        tooltip={
                          data?.internalNotes
                            ?.map(
                              (note, i) =>
                                `${note.user.firstName} ${
                                  note.user.lastName
                                }: ${note.note}<br />(${formatDateTimeFull(
                                  note.createdAt
                                )})${
                                  i < (data?.internalNotes?.length || 0) - 1
                                    ? '<br /><br />'
                                    : ''
                                }`
                            )
                            ?.join('') ?? ''
                        }
                      />
                      <Icon $rotate={internalNotesCollapse ? '180deg' : '0'}>
                        <ChevronSmall />
                      </Icon>
                    </Div>
                    <CollapsibleComponent
                      expanded={internalNotesCollapse}
                      elementHeight={elementHeight}
                      marginTop="4px"
                    >
                      <Div ref={ref} $flex $column $gap="12px">
                        <P $m="0" $mt="4">
                          {data?.internalNotes?.[0]?.note ?? '-'}
                        </P>
                        <Div $flex $justifyEnd>
                          <Button
                            $selected
                            $gray
                            $small
                            $w="100px"
                            onClick={() => setShowInternalNotesModal(true)}
                            style={{ margin: '0 2px 5px 0' }}
                          >
                            {data?.internalNotes?.[0]?.note ? 'Edit' : 'Add'}
                          </Button>
                        </Div>
                      </Div>
                    </CollapsibleComponent>
                  </Div>
                )}

                {isAdmin && interceptorCallData?.status && (
                  <Div $flex $column $p="12px 0" $borderBottom>
                    <Div
                      $pointer
                      $flex
                      $alignCenter
                      $between
                      onClick={() =>
                        setInterceptorCallCollapse((prev) => !prev)
                      }
                    >
                      <InfoComponent
                        hasBorderBottom={false}
                        Icon={GrayCircleEarphoneIcon}
                        title="AI Agent Outbound Call Recording"
                        padding="0"
                      />
                      <Icon $rotate={interceptorCallCollapse ? '180deg' : '0'}>
                        <ChevronSmall />
                      </Icon>
                    </Div>
                    <CollapsibleComponent
                      expanded={interceptorCallCollapse}
                      elementHeight={elementHeightInterceptorCall}
                      marginTop="16px"
                    >
                      <Div ref={refInterceptorCall} $flex $column $gap="12px">
                        <CallRecordingAndTranscription
                          interceptorCallData={interceptorCallData}
                          customerData={data?.customer}
                        />
                      </Div>
                    </CollapsibleComponent>
                  </Div>
                )}

                {isAdmin && isManual && (
                  <InfoComponent
                    Icon={OrderCustomerIcon}
                    title="Created By"
                    text={`${data?.createdBy?.firstName} ${data?.createdBy?.lastName}`}
                    component={
                      <Icon
                        $pointer
                        onClick={() =>
                          navigate(`/users/${data?.createdBy?.id}`)
                        }
                      >
                        <ArrowRed />
                      </Icon>
                    }
                  />
                )}

                {isAdmin && (
                  <InfoComponent
                    Icon={DollarIcon}
                    title="Amount"
                    hasBorderBottom={false}
                    component={
                      <P $m="0" $right>
                        Tip: {displayCentsInDollars(data?.charges?.tips)}
                        <br />
                        DSP Fee: {displayCentsInDollars(data?.charges?.dspFee)}
                        <DebtRepaymentLine charges={data?.charges} />
                      </P>
                    }
                    alignCenter={false}
                    alignStart
                  />
                )}
                {!isAdmin ? (
                  data?.brand.type !== BrandType.vGrubs ||
                  data?.brand.isLocation ? (
                    <InfoComponent
                      Icon={DollarIcon}
                      title="Amount"
                      hasBorderBottom={false}
                      component={
                        <P $m="0" $right>
                          Tip: {displayCentsInDollars(data?.charges?.tips)}
                          <br />
                          DSP Fee:{' '}
                          {displayCentsInDollars(data?.charges?.dspFee)}
                          <DebtRepaymentLine charges={data?.charges} />
                        </P>
                      }
                      alignCenter={false}
                      alignStart
                    />
                  ) : (
                    <InfoComponent
                      Icon={DollarIcon}
                      title="Amount"
                      hasBorderBottom={false}
                      component={
                        <P $m="0" $right>
                          Tip: -
                          <br />
                          DSP Fee: -
                          <DebtRepaymentLine charges={data?.charges} />
                        </P>
                      }
                      alignCenter={false}
                      alignStart
                    />
                  )
                ) : null}
              </Div>
            </Div>
            <Div $width="55%" $widthSM="100%">
              <Div $box $p="24px">
                <Div
                  $flex
                  $alignCenter
                  $between
                  $borderBottom
                  $mb="24"
                  $p="0 0 24px 0"
                >
                  <H5 $mb="0">Map Track</H5>
                  <DistancePill
                    miles={data?.distanceMiles}
                    dspMiles={data?.dspDistanceMiles}
                  />
                  <DeliveryStatusPill
                    status={data?.status}
                    cancellationReason={data?.cancellationReason}
                  />
                </Div>
                <Div $mb="24">
                  <DeliveryProgressBar status={data?.status} />
                </Div>
                <Suspense fallback={null}>
                  <DeliveryMapComponent data={data} />
                </Suspense>

                <Div>
                  {(isAdmin || isPartner) &&
                    orderData?.delivery?.type === DeliveryType.restaurant &&
                    orderData?.vDriveEnabled &&
                    orderData?.vDrive && (
                      <VDriveStatusComponent
                        m="24px 0 0 0"
                        mSM="20px 0 0 0"
                        vDrive={orderData?.vDrive}
                        brand={orderData?.brand}
                        id={orderData?.id}
                        getDrivers={getDrivers}
                        handleGetCancelVDriveOrderFee={
                          handleGetCancelVDriveOrderFee
                        }
                        setChangeAddressModal={setChangeAddressModal}
                      />
                    )}
                </Div>

                <H5 $m="24px 0 0 0">Delivery Timeline</H5>

                <TimelineComponent
                  text={'Requested'}
                  time={data?.timestamps?.created_at}
                  isFirst
                  status={data?.status}
                  timeZone={data?.timeZone}
                />

                <TimelineComponent
                  text={'Assigned'}
                  time={data?.timestamps?.runner_assigned_at}
                  status={data?.status}
                  timeZone={data?.timeZone}
                />

                <TimelineComponent
                  text={'Arrived at merchant'}
                  time={data?.timestamps?.runner_arrived_origin}
                  status={data?.status}
                  timeZone={data?.timeZone}
                />

                <TimelineComponent
                  text={'Pick up'}
                  time={data?.timestamps?.runner_picked_up}
                  status={data?.status}
                  timeZone={data?.timeZone}
                />

                <TimelineComponent
                  text={'Arrived at drop'}
                  time={data?.timestamps?.runner_arrived_dropoff}
                  status={data?.status}
                  timeZone={data?.timeZone}
                />

                <TimelineComponent
                  text={'Dropoff'}
                  time={data?.timestamps?.runner_dropped_off}
                  status={data?.status}
                  timeZone={data?.timeZone}
                />

                <TimelineComponent
                  text={'Cancelled'}
                  time={data?.timestamps?.cancelled_at}
                  status={data?.status}
                  timeZone={data?.timeZone}
                />

                <TimelineComponent
                  text={'Pickup Estimate'}
                  time={data?.timestamps?.pickup_estimate}
                  estimate={isFutureDate(data?.timestamps?.pickup_estimate)}
                  status={data?.status}
                  timeZone={data?.timeZone}
                />

                <TimelineComponent
                  text={'Dropoff Estimate'}
                  time={data?.timestamps?.dropoff_estimate}
                  estimate={isFutureDate(data?.timestamps?.dropoff_estimate)}
                  isLast
                  status={data?.status}
                  timeZone={data?.timeZone}
                />
              </Div>
            </Div>
          </Div>
          <DeliveryCancelModal
            setLoading={setLoading}
            id={id}
            isOpen={cancelModal}
            setIsOpen={setCancelModal}
            setData={setData}
            fee={cancelVDriveDeliveryFee}
            isDriverAssigned={isDriverAssigned}
          />
          <DeliveriesRequestRefund
            setLoading={setLoading}
            id={id}
            isOpen={refundModal}
            setIsOpen={setRefundModal}
          />

          <DeliveryInternalNotesModal
            modalIsOpen={showInternalNotesModal}
            closeModal={() => setShowInternalNotesModal(false)}
            setLoading={setLoading}
            setData={setData}
            data={data}
          />
          <ClaimDeliveryModal
            modalIsOpen={claimDeliveryModal}
            closeModal={() => setClaimDeliveryModal(null)}
            handleClaimDelivery={handleAssignDispatcher}
          />

          {orderData && (
            <VDriveAssignDriver
              order={orderData}
              setOrder={setOrderData}
              modalIsOpen={assignDriverModal}
              closeModal={() => {
                setAssignDriverModal(null);
              }}
              noDriverModal={noDriverModal}
              setNoDriverModal={setNoDriverModal}
              setLoading={setLoading}
            />
          )}

          <CancelVDriveModal
            cancelVDriveModal={cancelVDriveModal}
            closeModal={() => setCancelVDriveModal(false)}
            setLoading={setLoading}
            setData={setOrderData}
            fee={cancelVDriveOrderFee}
            isDriverAssigned={isDriverAssigned}
            id={orderData?.id?.toString() || ''}
          />

          <ChangeAddress
            modalIsOpen={changeAddressModal}
            closeModal={() => setChangeAddressModal(null)}
            setLoading={setLoading}
            setData={setOrderData}
          />
          <HistoryAssign
            closeModal={() => setHistoryAssignModal(null)}
            historyAssignModal={historyAssignModal}
            chosenVDriveProvider={data?.provider ?? null}
            chosenVDriveSource={data?.vDriveSource ?? null}
          />
        </>
      )}
    </>
  );
};

const TimelineComponent = ({
  text,
  time,
  estimate,
  hasBorderTop,
  hasBorderBottom = true,
  isFirst,
  isLast,
  status,
  timeZone,
}: {
  text: string;
  time?: string | null;
  estimate?: boolean;
  hasBorderTop?: boolean;
  hasBorderBottom?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  status?: vDriveDeliveryStatus;
  timeZone?: string;
}) => {
  const isDeliveryInProgress =
    status !== 'completed' &&
    status !== 'cancelled' &&
    status !== 'returned' &&
    status !== 'unknown';

  const renderTimeColor = () => {
    if (!isDeliveryInProgress) return lightTheme.darkGray;
    if (
      text === 'Dropoff Estimate' ||
      text === 'Pickup Estimate' ||
      text === 'Pickup' ||
      text === 'Dropoff'
    ) {
      if (isFutureDate(time)) {
        return lightTheme.green;
      } else {
        return lightTheme.primary;
      }
    } else return lightTheme.darkGray;
  };

  if (!time) return null;
  return (
    <Div
      $relative
      $borderTop={hasBorderTop}
      $borderBottom={hasBorderBottom}
      $flex
      $between
      $gap="16px"
      $alignCenter
      $ml="24"
      $height="60px"
      $heightSM="60px"
    >
      {!isFirst && (
        <TimelineLineTop $color={estimate ? lightTheme.lightGray : null} />
      )}
      {!isLast && (
        <TimelineLineBottom $color={estimate ? lightTheme.lightGray : null} />
      )}
      <TimelineCircle $color={estimate ? lightTheme.lightGray : null} />

      <Div $flex $alignCenter $gap="16px">
        <div>
          <P $bold $mb="0" style={{ opacity: estimate ? 0.5 : 1 }}>
            {text}
          </P>
        </div>
      </Div>
      <div>
        <Disclaimer $right $m="0" $color={renderTimeColor()}>
          {formatDateTimeShort(time, timeZone)}
        </Disclaimer>
        <Disclaimer $right $m="0" $color={renderTimeColor()}>
          ({getTimeAgoTimeIn(time)})
        </Disclaimer>
      </div>
    </Div>
  );
};

const InfoComponent = ({
  Icon,
  title,
  text,
  component,
  hasBorderTop,
  hasBorderBottom = true,
  padding = '12px 0',
  alignCenter = true,
  alignStart = false,
  tooltip,
}: {
  Icon: () => JSX.Element;
  title: string;
  text?: string | JSX.Element | number | null;
  component?: React.ReactNode;
  hasBorderTop?: boolean;
  hasBorderBottom?: boolean;
  alignCenter?: boolean;
  alignStart?: boolean;
  padding?: string;
  tooltip?: string;
}) => {
  return (
    <Div
      $borderTop={hasBorderTop}
      $borderBottom={hasBorderBottom}
      $p={padding}
      $flex
      $between
      $gap="16px"
      $alignCenter={alignCenter}
      $alignStart={alignStart}
    >
      <Div $flex $alignCenter $gap="16px">
        <Div $flex $flexNone>
          <Icon />
        </Div>
        <div>
          <Div $flex $alignCenter $gap="4px">
            <H5 $mb="0">{title}</H5>
            {tooltip && <TooltipIcon tooltip={tooltip} />}
          </Div>
          <P $mb="0">{text}</P>
        </div>
      </Div>
      {component}
    </Div>
  );
};

/**
 * What a debt repayment plan charged on this delivery, under the fees. It is
 * not part of the delivery's total, and nothing shows while it is 0.
 */
const DebtRepaymentLine = ({ charges }: { charges?: Charges | null }) =>
  charges?.debtRepayment ? (
    <>
      <br />
      Previous Transaction Due Balance:{' '}
      {displayCentsInDollars(charges.debtRepayment)}
    </>
  ) : null;
